// WHSSiteRiskAssessment.tsx
// WHS Priority 13 — Site Risk Assessment (SRA)
// Dynamic per-shift document completed before work commences each day.
// Distinct from the SWMS (task-specific) and Take 5 (individual worker).
// The SRA covers the full site at shift start — conditions, hazards, controls,
// people, plant, communications, and emergency readiness for that shift.
//
// WHS Regulation 2011 (Qld) s.34 — how to manage WHS risks
// Feeds: Toolbox talks, WHS inspections, incident log, job activity register

import { useState, useMemo } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { buildDocumentHeader, buildDocumentFooter } from '../utils/documentBranding'
import {
  AlertTriangle, CheckCircle, Clock, Download, Plus, X,
  Sun, Cloud, Wind, Thermometer, Users, Truck, Radio,
  ChevronDown, ChevronUp, ChevronRight, Shield, FileText, Search,
} from 'lucide-react'

// ── Types ─────────────────────────────────────────────────────────────────

type SRAStatus    = 'OPEN' | 'REVIEWED' | 'CLOSED'
type ShiftType    = 'DAY' | 'NIGHT' | 'SWING'
type WeatherCond  = 'CLEAR' | 'CLOUDY' | 'RAIN' | 'HEAVY_RAIN' | 'WINDY' | 'EXTREME_HEAT'

interface HazardCheck {
  id:       string
  label:    string
  category: string
  present:  boolean
  control:  string
}

interface SiteRiskAssessment {
  id:          string
  sraNumber:   string
  jobId:       string
  jobNumber:   string
  siteAddress: string
  shiftType:   ShiftType
  assessmentDate: string
  shiftStart:  string
  status:      SRAStatus
  // Section B — Environmental
  weather:     WeatherCond
  temperature: number      // degrees C
  windSpeed:   string      // e.g. 'Calm', '<20 km/h', '20–40 km/h', '>40 km/h (STOP WORK)'
  lighting:    string      // 'Daylight' | 'Twilight' | 'Artificial — check lux'
  siteConditions: string
  // Section C — Hazards (checklist)
  hazards:     HazardCheck[]
  // Section D — Controls confirmed
  swmsActive:          boolean
  swmsRef:             string
  trafficControlActive: boolean
  tmrActive:           string
  permitsHeld:         boolean
  permitsRef:          string
  exclusionZonesSet:   boolean
  // Section E — Plant on site
  plantOnSite:         string[]
  // Section F — Personnel
  workerCount:         number
  supervisorName:      string
  whiteCardsConfirmed: boolean
  inductionsConfirmed: boolean
  // Section G — Emergency
  firstAidKitLocation: string
  firstAidOfficerName: string
  emergencyPhone:      string
  musterPoint:         string
  // Section H — Sign-off
  preparedByName:  string
  preparedAt:      string
  reviewedByName:  string | null
  reviewedAt:      string | null
  closedAt:        string | null
  additionalNotes: string
}

// ── Metadata ─────────────────────────────────────────────────────────────

const STATUS_META: Record<SRAStatus, { label: string; bg: string; text: string }> = {
  OPEN:     { label: 'Open — pending review', bg: 'bg-amber-100', text: 'text-amber-700' },
  REVIEWED: { label: 'Reviewed',              bg: 'bg-blue-100',  text: 'text-blue-700'  },
  CLOSED:   { label: 'Closed',                bg: 'bg-green-100', text: 'text-green-700' },
}

const WEATHER_META: Record<WeatherCond, { label: string; icon: string; alert?: string }> = {
  CLEAR:       { label: 'Clear',                icon: '☀️' },
  CLOUDY:      { label: 'Cloudy',               icon: '☁️' },
  RAIN:        { label: 'Rain',                 icon: '🌧️' },
  HEAVY_RAIN:  { label: 'Heavy rain',           icon: '⛈️', alert: 'Consider work suspension — slippery surfaces, reduced visibility' },
  WINDY:       { label: 'Windy (>40 km/h)',     icon: '💨', alert: 'Elevated work & crane operations — assess against manufacturer limits' },
  EXTREME_HEAT:{ label: 'Extreme heat (>35°C)', icon: '🔥', alert: 'Heat stress management — hydration, shade, reduced work cycles' },
}

const DEFAULT_HAZARD_CHECKLIST = (): HazardCheck[] => [
  { id: 'h1',  category: 'Electrical',        label: 'Energised electrical infrastructure on or near site', present: false, control: '' },
  { id: 'h2',  category: 'Traffic',           label: 'Live traffic adjacent to work area',                  present: false, control: '' },
  { id: 'h3',  category: 'Traffic',           label: 'Pedestrians or cyclists in work zone',                present: false, control: '' },
  { id: 'h4',  category: 'Excavation',        label: 'Excavations or pits (confined space risk)',           present: false, control: '' },
  { id: 'h5',  category: 'Excavation',        label: 'Underground services (gas, water, comms, HV)',        present: false, control: '' },
  { id: 'h6',  category: 'Working at Height', label: 'Work above 2m (poles, EWP, rooftops)',                present: false, control: '' },
  { id: 'h7',  category: 'Plant',             label: 'Mobile plant or cranes operating on site',            present: false, control: '' },
  { id: 'h8',  category: 'Manual Handling',   label: 'Heavy or awkward lifts (>16 kg)',                     present: false, control: '' },
  { id: 'h9',  category: 'Chemical',          label: 'Hazardous substances (fuel, solvents, battery acid)',  present: false, control: '' },
  { id: 'h10', category: 'Environmental',     label: 'Watercourse or stormwater drain within 20m',          present: false, control: '' },
  { id: 'h11', category: 'Public interface',  label: 'Members of public in immediate proximity',             present: false, control: '' },
  { id: 'h12', category: 'Night works',       label: 'Low light or night-time conditions',                  present: false, control: '' },
]

// ── Helpers ──────────────────────────────────────────────────────────────

const fmt  = (s?: string | null) =>
  s ? new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit' }) : '—'
const fmtT = (s?: string | null) =>
  s ? new Date(s).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'
const NOW = new Date().toISOString()
const dAgo = (h: number) => new Date(Date.now() - h * 3600000).toISOString()

// ── Seed data ─────────────────────────────────────────────────────────────

const seedHazards = (presentIds: string[], controls: Record<string, string> = {}): HazardCheck[] =>
  DEFAULT_HAZARD_CHECKLIST().map(h => ({
    ...h,
    present: presentIds.includes(h.id),
    control: controls[h.id] ?? (presentIds.includes(h.id) ? 'Controls in place per SWMS' : ''),
  }))

const SEED_SRAS: SiteRiskAssessment[] = [
  {
    id: 'sra-001', sraNumber: 'SRA-TP001-20260808-D',
    jobId: 'j1', jobNumber: 'TP-2026-001', siteAddress: 'Sandgate Road, Banyo QLD 4014',
    shiftType: 'DAY', assessmentDate: new Date().toISOString().split('T')[0], shiftStart: '06:00',
    status: 'REVIEWED',
    weather: 'CLEAR', temperature: 24, windSpeed: '<20 km/h', lighting: 'Daylight',
    siteConditions: 'Pit 4B reopened from yesterday. Ground dry. Sandgate Rd NB lane closure active per TMP-TP001-001. VMS boards operational.',
    hazards: seedHazards(['h1','h2','h4','h5','h7'], {
      h1: 'Isolation confirmed and tagged — LOTO applied. ESO on site.',
      h2: 'TMP-TP001-001 active. 2 TC officers at NB and SB approaches. 40km/h enforced.',
      h4: 'CS entry permit required. Atmospheric testing pre-entry. Tripod and harness on site.',
      h5: 'DBYD plans reviewed. Vacuum excavation first 500mm. All services marked.',
      h7: 'EWP pre-start completed. Operator VOC current. Exclusion zone 3m established.',
    }),
    swmsActive: true, swmsRef: 'SWMS-TP001-001 (HV termination), SWMS-TP001-002 (confined space)',
    trafficControlActive: true, tmrActive: 'TMP-TP001-001 v2 (approved TMR)',
    permitsHeld: true, permitsRef: 'PTW-CS-2026-014 (confined space) + PTW-EE-2026-022 (energised)',
    exclusionZonesSet: true,
    plantOnSite: ['Isuzu FRR crew truck (rego: 123ABC)', 'EWP — Genie Z-45 (rego: 456DEF)', 'Generator — Honda 6.5kVA'],
    workerCount: 6, supervisorName: 'Tom Richards', whiteCardsConfirmed: true, inductionsConfirmed: true,
    firstAidKitLocation: 'Crew truck — driver side door', firstAidOfficerName: 'Tom Richards',
    emergencyPhone: '000 / Tom Richards 0412 345 678 / Jane Smith 0421 987 654',
    musterPoint: 'BP Service Station carpark, Sandgate Rd (primary)',
    preparedByName: 'Tom Richards', preparedAt: dAgo(8),
    reviewedByName: 'Jane Smith', reviewedAt: dAgo(7),
    closedAt: null, additionalNotes: 'Electrical Safety Officer on site for HV termination all day.',
  },
  {
    id: 'sra-002', sraNumber: 'SRA-TP003-20260807-N',
    jobId: 'j3', jobNumber: 'TP-2026-003', siteAddress: 'Main St & Church St, Maroochydore QLD 4558',
    shiftType: 'NIGHT', assessmentDate: new Date(Date.now() - 86400000).toISOString().split('T')[0], shiftStart: '19:00',
    status: 'CLOSED',
    weather: 'CLOUDY', temperature: 18, windSpeed: '<20 km/h', lighting: 'Artificial — check lux',
    siteConditions: 'Night shift for pole foundation augering. Intersection closed 19:00–23:00 per TMP-TP003-001. Site lighting: 4× 1000W tower lights. Lux measured at 340 lux at work face — compliant.',
    hazards: seedHazards(['h2','h3','h6','h7','h12'], {
      h2: 'Full intersection closure per TMP-TP003-001 (Council approved). All approaches controlled.',
      h3: 'Pedestrian detour in place with pedestrian TC officer. Barriers at 10m from work zone.',
      h6: 'Pole work to 6m max. Safety harness and hard hat mandatory. EWP for elevated access.',
      h7: 'Auger rig — Junttan PM-22. Pre-start completed. Operator Tony Halley (VOC current).',
      h12: 'Tower lights × 4, 340 lux measured — compliant. Hi-vis Class D mandatory.',
    }),
    swmsActive: true, swmsRef: 'SWMS-TP003-001 (pole erection), SWMS-TP003-003 (night traffic mgmt)',
    trafficControlActive: true, tmrActive: 'TMP-TP003-001 v1 (Council — pending approval)',
    permitsHeld: false, permitsRef: 'No PTWs required for this task set',
    exclusionZonesSet: true,
    plantOnSite: ['Junttan PM-22 auger rig (rego: AUG-002)', 'Hiab crane truck (rego: HTK-441)', '4× Tower lights'],
    workerCount: 8, supervisorName: 'Sarah Lee', whiteCardsConfirmed: true, inductionsConfirmed: true,
    firstAidKitLocation: 'Crane truck — passenger side', firstAidOfficerName: 'Sarah Lee',
    emergencyPhone: '000 / Sarah Lee 0432 991 234 / Mike Walsh 0411 774 329',
    musterPoint: 'Woolworths carpark, Aerodrome Road (NW corner)',
    preparedByName: 'Sarah Lee', preparedAt: dAgo(30),
    reviewedByName: 'Mike Walsh', reviewedAt: dAgo(29),
    closedAt: dAgo(24), additionalNotes: 'Night shift completed without incident. Noise level within DES limit (58 dB(A)).',
  },
  {
    id: 'sra-003', sraNumber: 'SRA-TP001-20260808-N',
    jobId: 'j1', jobNumber: 'TP-2026-001', siteAddress: 'Sandgate Road, Banyo QLD 4014',
    shiftType: 'NIGHT', assessmentDate: new Date().toISOString().split('T')[0], shiftStart: '21:00',
    status: 'OPEN',
    weather: 'CLEAR', temperature: 19, windSpeed: '<20 km/h', lighting: 'Artificial — check lux',
    siteConditions: 'Night shift — carriageway reinstatement. Asphalt crew mobilising 21:30. Full NB lane closure 21:00–05:00.',
    hazards: DEFAULT_HAZARD_CHECKLIST().map(h => ({ ...h, present: false, control: '' })),
    swmsActive: false, swmsRef: '',
    trafficControlActive: false, tmrActive: '',
    permitsHeld: false, permitsRef: '',
    exclusionZonesSet: false,
    plantOnSite: [], workerCount: 0, supervisorName: '',
    whiteCardsConfirmed: false, inductionsConfirmed: false,
    firstAidKitLocation: '', firstAidOfficerName: '', emergencyPhone: '', musterPoint: '',
    preparedByName: '', preparedAt: NOW,
    reviewedByName: null, reviewedAt: null, closedAt: null, additionalNotes: '',
  },
]

// ── PDF generation ────────────────────────────────────────────────────────

function generateSRAPDF(sra: SiteRiskAssessment) {
  const presentHazards = sra.hazards.filter(h => h.present)
  const now = new Date().toLocaleDateString('en-AU', { day: '2-digit', month: 'long', year: 'numeric' })

  const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
<title>Site Risk Assessment — ${sra.sraNumber}</title>
<style>
*{box-sizing:border-box}
body{font-family:Arial,sans-serif;font-size:10pt;color:#111;margin:18mm 20mm}
.doc-title{border-bottom:3px solid #1F3864;padding-bottom:8px;margin-bottom:14px}
h2{font-size:14pt;font-weight:900;color:#1F3864;margin:0 0 2px;text-transform:uppercase}
.subtitle{font-size:9pt;color:#555}
.sec-hdr{font-size:11pt;font-weight:bold;color:#1F3864;border-left:4px solid #2E75B6;padding-left:8px;margin:16px 0 8px;text-transform:uppercase;letter-spacing:.5px}
.grid2{display:grid;grid-template-columns:1fr 1fr;gap:0;border:1px solid #ccc;margin-bottom:14px}
.cell{padding:5px 10px;border-bottom:1px solid #eee;font-size:9pt}
.cell:nth-child(odd){background:#f7f8fa;font-weight:bold;color:#555}
.hazard-table{width:100%;border-collapse:collapse;margin-bottom:14px;font-size:9pt}
.hazard-table th{background:#1F3864;color:#fff;padding:5px 8px;text-align:left}
.hazard-table td{padding:5px 8px;border-bottom:1px solid #eee}
.hazard-table tr:nth-child(even){background:#f7f8fa}
.check-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-bottom:14px}
.check-item{border:1px solid #ccc;border-radius:3px;padding:6px 8px;font-size:8.5pt}
.check-yes{border-color:#4CAF50;background:#E8F5E9;color:#1B5E20}
.check-no{border-color:#F44336;background:#FFEBEE;color:#B71C1C}
.weather-strip{background:#E3F2FD;border:1px solid #90CAF9;border-radius:4px;padding:8px 12px;margin-bottom:12px;font-size:9.5pt}
.alert-box{background:#FFF3E0;border:1.5px solid #FB8C00;border-radius:4px;padding:8px 12px;margin-bottom:12px;font-size:9.5pt;color:#E65100}
.sig-grid{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-top:20px}
.sig-box{border:1.5px solid #999;border-radius:4px;padding:12px;min-height:70px}
.sig-lbl{font-size:8pt;font-weight:bold;color:#555;text-transform:uppercase;margin-bottom:4px}
.sig-line{border-bottom:1px solid #555;height:24px;margin:8px 0 4px}
.sig-name{font-size:8pt;color:#777}
@media print{body{margin:14mm}}
</style></head><body>
${buildDocumentHeader('INVOICE')}
<div class="doc-title">
  <h2>Site Risk Assessment</h2>
  <div class="subtitle">WHS Regulation 2011 (Qld) s.34 &mdash; Risk Management &nbsp;&bull;&nbsp; Completed at shift start</div>
</div>

<div class="sec-hdr">A — Site and shift details</div>
<div class="grid2">
  <div class="cell">SRA number</div>   <div class="cell">${sra.sraNumber}</div>
  <div class="cell">Job number</div>   <div class="cell">${sra.jobNumber}</div>
  <div class="cell">Site address</div> <div class="cell">${sra.siteAddress}</div>
  <div class="cell">Date</div>         <div class="cell">${fmt(sra.assessmentDate)}</div>
  <div class="cell">Shift</div>        <div class="cell">${sra.shiftType} — Start: ${sra.shiftStart}</div>
  <div class="cell">Status</div>       <div class="cell">${sra.status}</div>
</div>

<div class="sec-hdr">B — Environmental conditions</div>
<div class="weather-strip">
  ${WEATHER_META[sra.weather].icon} &nbsp;
  <strong>Weather:</strong> ${WEATHER_META[sra.weather].label} &nbsp;&bull;&nbsp;
  <strong>Temp:</strong> ${sra.temperature}°C &nbsp;&bull;&nbsp;
  <strong>Wind:</strong> ${sra.windSpeed} &nbsp;&bull;&nbsp;
  <strong>Lighting:</strong> ${sra.lighting}
</div>
${WEATHER_META[sra.weather].alert ? `<div class="alert-box">⚠ ${WEATHER_META[sra.weather].alert}</div>` : ''}
<div class="cell" style="border:1px solid #ccc;padding:8px 10px;margin-bottom:14px;font-size:9pt">${sra.siteConditions || '(No site conditions recorded)'}</div>

<div class="sec-hdr">C — Hazards identified and controls</div>
${presentHazards.length > 0 ? `
<table class="hazard-table">
  <tr><th>#</th><th>Hazard</th><th>Category</th><th>Control measures</th></tr>
  ${presentHazards.map((h, i) => `<tr><td>${i+1}</td><td>${h.label}</td><td>${h.category}</td><td>${h.control || 'See SWMS'}</td></tr>`).join('')}
</table>` : '<p style="font-size:9pt;color:#555;margin-bottom:14px">No hazards identified for this shift.</p>'}

<div class="sec-hdr">D — Control measures confirmed</div>
<div class="check-grid">
  <div class="check-item ${sra.swmsActive ? 'check-yes' : 'check-no'}">${sra.swmsActive ? '✓' : '✗'} SWMS active<br><small>${sra.swmsRef || 'Not recorded'}</small></div>
  <div class="check-item ${sra.trafficControlActive ? 'check-yes' : 'check-no'}">${sra.trafficControlActive ? '✓' : '✗'} Traffic control active<br><small>${sra.tmrActive || 'N/A'}</small></div>
  <div class="check-item ${sra.permitsHeld ? 'check-yes' : 'check-no'}">${sra.permitsHeld ? '✓' : '✗'} Permits held<br><small>${sra.permitsRef || 'None required'}</small></div>
  <div class="check-item ${sra.exclusionZonesSet ? 'check-yes' : 'check-no'}">${sra.exclusionZonesSet ? '✓' : '✗'} Exclusion zones set</div>
</div>

<div class="sec-hdr">E — Plant on site</div>
<div class="cell" style="border:1px solid #ccc;padding:8px 10px;margin-bottom:14px;font-size:9pt">${sra.plantOnSite.length ? sra.plantOnSite.join('<br>') : 'No plant on site this shift'}</div>

<div class="sec-hdr">F — Personnel</div>
<div class="grid2">
  <div class="cell">Workers on site</div>   <div class="cell">${sra.workerCount}</div>
  <div class="cell">Shift supervisor</div>  <div class="cell">${sra.supervisorName || '—'}</div>
  <div class="cell">White cards confirmed</div> <div class="cell">${sra.whiteCardsConfirmed ? '✓ Yes' : '✗ No'}</div>
  <div class="cell">Inductions confirmed</div>   <div class="cell">${sra.inductionsConfirmed ? '✓ Yes' : '✗ No'}</div>
</div>

<div class="sec-hdr">G — Emergency readiness</div>
<div class="grid2">
  <div class="cell">First aid kit</div>   <div class="cell">${sra.firstAidKitLocation || '—'}</div>
  <div class="cell">First aid officer</div> <div class="cell">${sra.firstAidOfficerName || '—'}</div>
  <div class="cell">Emergency contacts</div> <div class="cell">${sra.emergencyPhone || '—'}</div>
  <div class="cell">Muster point</div> <div class="cell">${sra.musterPoint || '—'}</div>
</div>

${sra.additionalNotes ? `<div class="sec-hdr">Additional notes</div>
<div class="cell" style="border:1px solid #ccc;padding:8px 10px;margin-bottom:14px;font-size:9pt">${sra.additionalNotes}</div>` : ''}

<div class="sig-grid">
  <div class="sig-box">
    <div class="sig-lbl">Prepared by (shift supervisor)</div>
    <div class="sig-line">${sra.preparedByName ? '✓ Signed' : ''}</div>
    <div class="sig-name">${sra.preparedByName || '—'}</div>
    <div class="sig-name" style="margin-top:4px">Time: ${fmtT(sra.preparedAt)}</div>
  </div>
  <div class="sig-box">
    <div class="sig-lbl">Reviewed by (WHSO / PM)</div>
    <div class="sig-line">${sra.reviewedByName ? '✓ Signed' : ''}</div>
    <div class="sig-name">${sra.reviewedByName || 'Pending review'}</div>
    <div class="sig-name" style="margin-top:4px">Time: ${fmtT(sra.reviewedAt)}</div>
  </div>
</div>

${buildDocumentFooter('INVOICE', sra.sraNumber)}
</body></html>`

  const w = window.open('', '_blank', 'width=1000,height=820')
  if (!w) { alert('Pop-up blocked'); return }
  w.document.write(html)
  w.document.close()
  setTimeout(() => { w.focus(); w.print() }, 350)
}

// ── SRA form (new assessment) ─────────────────────────────────────────────

function SRAForm({ jobNumber, siteAddress, shiftType, onSave, onCancel, preparedByName }:
  { jobNumber: string; siteAddress: string; shiftType: ShiftType; onSave: (sra: SiteRiskAssessment) => void; onCancel: () => void; preparedByName: string }
) {
  const [weather, setWeather]         = useState<WeatherCond>('CLEAR')
  const [temp, setTemp]               = useState(22)
  const [wind, setWind]               = useState('<20 km/h')
  const [lighting, setLighting]       = useState(shiftType === 'NIGHT' ? 'Artificial — check lux' : 'Daylight')
  const [conditions, setConditions]   = useState('')
  const [hazards, setHazards]         = useState<HazardCheck[]>(DEFAULT_HAZARD_CHECKLIST())
  const [swmsActive, setSwmsActive]   = useState(false)
  const [swmsRef, setSwmsRef]         = useState('')
  const [tcActive, setTcActive]       = useState(false)
  const [tmRef, setTmRef]             = useState('')
  const [permits, setPermits]         = useState(false)
  const [permitsRef, setPermitsRef]   = useState('')
  const [zones, setZones]             = useState(false)
  const [plant, setPlant]             = useState('')
  const [workers, setWorkers]         = useState(1)
  const [supervisor, setSupervisor]   = useState(preparedByName)
  const [whiteCards, setWhiteCards]   = useState(false)
  const [inductions, setInductions]   = useState(false)
  const [faLoc, setFaLoc]             = useState('')
  const [faOfficer, setFaOfficer]     = useState('')
  const [emerPhone, setEmerPhone]     = useState('000')
  const [muster, setMuster]           = useState('')
  const [notes, setNotes]             = useState('')

  const toggleHazard = (id: string) =>
    setHazards(prev => prev.map(h => h.id === id ? { ...h, present: !h.present } : h))
  const setControl = (id: string, control: string) =>
    setHazards(prev => prev.map(h => h.id === id ? { ...h, control } : h))

  const isReady = conditions.trim().length > 0 && supervisor.trim().length > 0

  const handleSave = () => {
    const now = new Date().toISOString()
    const dateStr = now.split('T')[0]
    const seq = Math.floor(Math.random() * 900) + 100
    const newSra: SiteRiskAssessment = {
      id: `sra-${Date.now()}`, sraNumber: `SRA-${jobNumber}-${dateStr.replace(/-/g,'').slice(2)}-${shiftType[0]}`,
      jobId: jobNumber, jobNumber, siteAddress, shiftType,
      assessmentDate: dateStr, shiftStart: shiftType === 'NIGHT' ? '21:00' : '06:00',
      status: 'OPEN',
      weather, temperature: temp, windSpeed: wind, lighting, siteConditions: conditions,
      hazards,
      swmsActive, swmsRef, trafficControlActive: tcActive, tmrActive: tmRef,
      permitsHeld: permits, permitsRef, exclusionZonesSet: zones,
      plantOnSite: plant.split('\n').filter(p => p.trim()),
      workerCount: workers, supervisorName: supervisor,
      whiteCardsConfirmed: whiteCards, inductionsConfirmed: inductions,
      firstAidKitLocation: faLoc, firstAidOfficerName: faOfficer,
      emergencyPhone: emerPhone, musterPoint: muster,
      preparedByName, preparedAt: now,
      reviewedByName: null, reviewedAt: null, closedAt: null, additionalNotes: notes,
    }
    onSave(newSra)
  }

  const presentHazards = hazards.filter(h => h.present)

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="bg-[#1F3864] text-white px-5 py-3 flex items-center justify-between">
        <div className="font-semibold text-sm">New Site Risk Assessment — {jobNumber} · {shiftType} shift</div>
        <button onClick={onCancel}><X className="w-4 h-4 text-white/70 hover:text-white" /></button>
      </div>

      <div className="p-5 space-y-5 max-h-[70vh] overflow-y-auto">

        {/* B — Environmental */}
        <div>
          <h3 className="text-xs font-bold text-[#1F3864] uppercase tracking-wide border-l-4 border-[#2E75B6] pl-2 mb-3">B — Environmental conditions</h3>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div>
              <label className="label-sm">Weather</label>
              <select value={weather} onChange={e => setWeather(e.target.value as WeatherCond)} className="input text-sm w-full">
                {Object.entries(WEATHER_META).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-sm">Temperature (°C)</label>
              <input type="number" value={temp} onChange={e => setTemp(+e.target.value)} className="input text-sm w-full" />
            </div>
            <div>
              <label className="label-sm">Wind speed</label>
              <select value={wind} onChange={e => setWind(e.target.value)} className="input text-sm w-full">
                <option>Calm</option>
                <option>&lt;20 km/h</option>
                <option>20–40 km/h</option>
                <option>&gt;40 km/h (STOP WORK)</option>
              </select>
            </div>
            <div>
              <label className="label-sm">Lighting</label>
              <select value={lighting} onChange={e => setLighting(e.target.value)} className="input text-sm w-full">
                <option>Daylight</option>
                <option>Twilight</option>
                <option>Artificial — check lux</option>
              </select>
            </div>
          </div>
          {WEATHER_META[weather].alert && (
            <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
              <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />{WEATHER_META[weather].alert}
            </div>
          )}
          <div className="mt-3">
            <label className="label-sm">Site conditions today</label>
            <textarea value={conditions} onChange={e => setConditions(e.target.value)} rows={2}
              placeholder="Describe current site conditions — access, surface state, any changed conditions from previous shift…"
              className="input text-sm w-full resize-none mt-1" />
          </div>
        </div>

        {/* C — Hazards */}
        <div>
          <h3 className="text-xs font-bold text-[#1F3864] uppercase tracking-wide border-l-4 border-[#2E75B6] pl-2 mb-3">C — Hazard identification</h3>
          <p className="text-xs text-gray-500 mb-3">Check all hazards present on site for this shift. For each checked hazard, confirm controls are in place.</p>
          <div className="space-y-1.5">
            {hazards.map(h => (
              <div key={h.id} className={`border rounded-lg transition-colors ${h.present ? 'border-amber-300 bg-amber-50/50' : 'border-gray-200'}`}>
                <div className="flex items-center gap-3 px-3 py-2">
                  <input type="checkbox" checked={h.present} onChange={() => toggleHazard(h.id)}
                    className="rounded border-gray-300 text-amber-500 focus:ring-amber-400" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-gray-800">{h.label}</div>
                    <div className="text-xs text-gray-400">{h.category}</div>
                  </div>
                </div>
                {h.present && (
                  <div className="px-3 pb-2">
                    <input type="text" value={h.control} onChange={e => setControl(h.id, e.target.value)}
                      placeholder="Confirm controls in place…"
                      className="input text-xs w-full" />
                  </div>
                )}
              </div>
            ))}
          </div>
          {presentHazards.length > 0 && (
            <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700">
              {presentHazards.length} hazard{presentHazards.length !== 1 ? 's' : ''} identified — confirm controls before commencing work.
            </div>
          )}
        </div>

        {/* D — Controls */}
        <div>
          <h3 className="text-xs font-bold text-[#1F3864] uppercase tracking-wide border-l-4 border-[#2E75B6] pl-2 mb-3">D — Control measures confirmed</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {[
              { label: 'SWMS active and briefed to crew', checked: swmsActive, setChecked: setSwmsActive, detail: swmsRef, setDetail: setSwmsRef, placeholder: 'SWMS reference(s)' },
              { label: 'Traffic control active per TMP', checked: tcActive, setChecked: setTcActive, detail: tmRef, setDetail: setTmRef, placeholder: 'TMP reference' },
              { label: 'Permits to Work held', checked: permits, setChecked: setPermits, detail: permitsRef, setDetail: setPermitsRef, placeholder: 'Permit references or N/A' },
            ].map(({ label, checked, setChecked, detail, setDetail, placeholder }) => (
              <div key={label} className={`border rounded-lg p-3 ${checked ? 'border-green-300 bg-green-50/40' : 'border-gray-200'}`}>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)}
                    className="rounded border-gray-300 text-green-600" />
                  <span className="text-sm text-gray-800">{label}</span>
                </label>
                {checked && (
                  <input type="text" value={detail} onChange={e => setDetail(e.target.value)}
                    placeholder={placeholder} className="input text-xs w-full mt-2" />
                )}
              </div>
            ))}
            <div className={`border rounded-lg p-3 ${zones ? 'border-green-300 bg-green-50/40' : 'border-gray-200'}`}>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={zones} onChange={e => setZones(e.target.checked)}
                  className="rounded border-gray-300 text-green-600" />
                <span className="text-sm text-gray-800">Exclusion zones established</span>
              </label>
            </div>
          </div>
        </div>

        {/* E — Plant */}
        <div>
          <h3 className="text-xs font-bold text-[#1F3864] uppercase tracking-wide border-l-4 border-[#2E75B6] pl-2 mb-2">E — Plant on site this shift</h3>
          <textarea value={plant} onChange={e => setPlant(e.target.value)} rows={3}
            placeholder="List each item of plant and equipment with registration or ID — one per line"
            className="input text-sm w-full resize-none" />
        </div>

        {/* F — Personnel */}
        <div>
          <h3 className="text-xs font-bold text-[#1F3864] uppercase tracking-wide border-l-4 border-[#2E75B6] pl-2 mb-3">F — Personnel</h3>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div>
              <label className="label-sm">Workers on site</label>
              <input type="number" value={workers} onChange={e => setWorkers(+e.target.value)} min={1} className="input text-sm w-full" />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="label-sm">Shift supervisor</label>
              <input value={supervisor} onChange={e => setSupervisor(e.target.value)} className="input text-sm w-full" />
            </div>
            <div className="flex items-center gap-2 pt-5">
              <input type="checkbox" checked={whiteCards} onChange={e => setWhiteCards(e.target.checked)} className="rounded" />
              <label className="text-sm text-gray-700">White cards confirmed</label>
            </div>
            <div className="flex items-center gap-2 pt-5">
              <input type="checkbox" checked={inductions} onChange={e => setInductions(e.target.checked)} className="rounded" />
              <label className="text-sm text-gray-700">Inductions confirmed</label>
            </div>
          </div>
        </div>

        {/* G — Emergency */}
        <div>
          <h3 className="text-xs font-bold text-[#1F3864] uppercase tracking-wide border-l-4 border-[#2E75B6] pl-2 mb-3">G — Emergency readiness</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-sm">First aid kit location</label>
              <input value={faLoc} onChange={e => setFaLoc(e.target.value)} placeholder="e.g. Crew truck — driver door" className="input text-sm w-full" />
            </div>
            <div>
              <label className="label-sm">First aid officer</label>
              <input value={faOfficer} onChange={e => setFaOfficer(e.target.value)} placeholder="Name + mobile" className="input text-sm w-full" />
            </div>
            <div>
              <label className="label-sm">Emergency contacts</label>
              <input value={emerPhone} onChange={e => setEmerPhone(e.target.value)} placeholder="000 / PM / WHSO" className="input text-sm w-full" />
            </div>
            <div>
              <label className="label-sm">Muster point</label>
              <input value={muster} onChange={e => setMuster(e.target.value)} placeholder="Primary assembly point" className="input text-sm w-full" />
            </div>
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="label-sm">Additional notes</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
            placeholder="Any additional hazards, special conditions, or instructions not covered above…"
            className="input text-sm w-full resize-none mt-1" />
        </div>
      </div>

      <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-between gap-3">
        <p className="text-xs text-gray-500">All workers must acknowledge this SRA before commencing work.</p>
        <div className="flex gap-2">
          <button onClick={onCancel} className="btn-secondary text-sm">Cancel</button>
          <button onClick={handleSave} disabled={!isReady}
            className="btn-primary text-sm disabled:opacity-40 flex items-center gap-2">
            <FileText className="w-4 h-4" />Submit SRA
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────

export function WHSSiteRiskAssessment() {
  const { isTier, user } = useAuthStore()
  const canReview = isTier('C_WHSO') || isTier('B_PM') || isTier('B_PC') || isTier('A_DEVELOPER')
  const canCreate = isTier('D_FIELD') || canReview

  const [sras, setSras]           = useState<SiteRiskAssessment[]>(SEED_SRAS)
  const [showNew, setShowNew]     = useState(false)
  const [selected, setSelected]   = useState<SiteRiskAssessment | null>(null)
  const [search, setSearch]       = useState('')
  const [filterStatus, setFilterStatus] = useState<SRAStatus | 'ALL'>('ALL')
  const [newJob, setNewJob]       = useState('TP-2026-001')
  const [newAddr, setNewAddr]     = useState('Sandgate Road, Banyo QLD 4014')
  const [newShift, setNewShift]   = useState<ShiftType>('DAY')

  const filtered = useMemo(() => sras.filter(s => {
    const ms = !search || s.jobNumber.toLowerCase().includes(search.toLowerCase()) || s.siteAddress.toLowerCase().includes(search.toLowerCase())
    const mf = filterStatus === 'ALL' || s.status === filterStatus
    return ms && mf
  }), [sras, search, filterStatus])

  const openCount     = sras.filter(s => s.status === 'OPEN').length
  const reviewedCount = sras.filter(s => s.status === 'REVIEWED').length

  const handleReview = (id: string) => {
    const patch = { status: 'REVIEWED' as SRAStatus, reviewedByName: user?.fullName ?? 'WHSO', reviewedAt: new Date().toISOString() }
    setSras(prev => prev.map(s => s.id === id ? { ...s, ...patch } : s))
    setSelected(prev => prev?.id === id ? { ...prev, ...patch } : prev)
  }

  const handleClose = (id: string) => {
    const patch = { status: 'CLOSED' as SRAStatus, closedAt: new Date().toISOString() }
    setSras(prev => prev.map(s => s.id === id ? { ...s, ...patch } : s))
    setSelected(prev => prev?.id === id ? { ...prev, ...patch } : prev)
  }

  const handleSaveSRA = (sra: SiteRiskAssessment) => {
    setSras(prev => [sra, ...prev])
    setSelected(sra)
    setShowNew(false)
  }

  if (showNew) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div>
            <label className="label-sm">Job number</label>
            <input value={newJob} onChange={e => setNewJob(e.target.value)} className="input text-sm w-36" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="label-sm">Site address</label>
            <input value={newAddr} onChange={e => setNewAddr(e.target.value)} className="input text-sm w-full" />
          </div>
          <div>
            <label className="label-sm">Shift type</label>
            <select value={newShift} onChange={e => setNewShift(e.target.value as ShiftType)} className="input text-sm">
              <option value="DAY">Day shift</option>
              <option value="NIGHT">Night shift</option>
              <option value="SWING">Swing shift</option>
            </select>
          </div>
        </div>
        <SRAForm
          jobNumber={newJob} siteAddress={newAddr} shiftType={newShift}
          preparedByName={user?.fullName ?? 'Field Worker'}
          onSave={handleSaveSRA} onCancel={() => setShowNew(false)}
        />
      </div>
    )
  }

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Shield className="w-5 h-5 text-[#2E75B6]" />
            Site Risk Assessments
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Per-shift document completed before work commences · WHS Reg 2011 (Qld) s.34</p>
        </div>
        {canCreate && (
          <button onClick={() => setShowNew(true)} className="btn-primary text-sm flex items-center gap-2">
            <Plus className="w-4 h-4" />New SRA
          </button>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total SRAs',   value: sras.length,   color: 'text-gray-900',  sub: 'all shifts' },
          { label: 'Open',         value: openCount,     color: openCount > 0 ? 'text-amber-700' : 'text-gray-400', sub: 'pending review' },
          { label: 'Reviewed',     value: reviewedCount, color: 'text-blue-700',  sub: 'WHSO reviewed' },
          { label: 'Closed',       value: sras.filter(s => s.status === 'CLOSED').length, color: 'text-green-700', sub: 'complete' },
        ].map(({ label, value, color, sub }) => (
          <div key={label} className="bg-white border border-gray-200 rounded-xl px-4 py-3">
            <div className={`text-2xl font-bold ${color}`}>{value}</div>
            <div className="text-xs font-medium text-gray-700 mt-0.5">{label}</div>
            <div className="text-xs text-gray-400">{sub}</div>
          </div>
        ))}
      </div>

      {openCount > 0 && canReview && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-center gap-3 text-sm text-amber-800">
          <Clock className="w-4 h-4 flex-shrink-0 text-amber-600" />
          <span><strong>{openCount} SRA{openCount !== 1 ? 's' : ''} awaiting WHSO review.</strong> Review each SRA before the next shift begins.</span>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search job or site…" className="input pl-9 text-sm w-full" />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value as any)} className="input text-sm w-40">
          <option value="ALL">All statuses</option>
          <option value="OPEN">Open</option>
          <option value="REVIEWED">Reviewed</option>
          <option value="CLOSED">Closed</option>
        </select>
      </div>

      {/* List + Detail */}
      <div className="flex gap-5 items-start">
        <div className={`${selected ? 'w-1/2' : 'w-full'} space-y-2 transition-all`}>
          {filtered.map(sra => {
            const sm      = STATUS_META[sra.status]
            const wm      = WEATHER_META[sra.weather]
            const hazCnt  = sra.hazards.filter(h => h.present).length
            const isSel   = selected?.id === sra.id
            return (
              <div key={sra.id}
                onClick={() => setSelected(isSel ? null : sra)}
                className={`bg-white border rounded-xl p-4 cursor-pointer transition-all hover:shadow-sm ${isSel ? 'border-[#2E75B6] ring-2 ring-blue-100' : sra.status === 'OPEN' ? 'border-amber-200' : 'border-gray-200 hover:border-gray-300'}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-xs font-mono text-gray-400">{sra.sraNumber}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sm.bg} ${sm.text}`}>{sm.label}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sra.shiftType === 'NIGHT' ? 'bg-purple-100 text-purple-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {sra.shiftType}
                      </span>
                    </div>
                    <div className="font-semibold text-gray-900 text-sm">{sra.jobNumber} — {sra.siteAddress}</div>
                    <div className="flex items-center gap-4 mt-1 text-xs text-gray-500 flex-wrap">
                      <span>{fmt(sra.assessmentDate)} · Start {sra.shiftStart}</span>
                      <span>{wm.icon} {wm.label} · {sra.temperature}°C</span>
                      <span>{sra.workerCount} worker{sra.workerCount !== 1 ? 's' : ''}</span>
                      {hazCnt > 0 && <span className="text-amber-600 font-medium">{hazCnt} hazard{hazCnt !== 1 ? 's' : ''}</span>}
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 flex-shrink-0 mt-1 ${isSel ? 'text-[#2E75B6]' : 'text-gray-400'}`} />
                </div>
              </div>
            )
          })}
          {filtered.length === 0 && (
            <div className="bg-white border border-dashed border-gray-300 rounded-xl py-12 text-center text-gray-400 text-sm">
              No SRAs match the current filter
            </div>
          )}
        </div>

        {selected && (
          <div className="w-1/2 sticky top-6 space-y-3" style={{ maxHeight: 'calc(100vh - 160px)', overflowY: 'auto' }}>
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              {/* Detail header */}
              <div className="px-4 py-3 border-b bg-gray-50 flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-xs font-mono text-gray-400">{selected.sraNumber}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_META[selected.status].bg} ${STATUS_META[selected.status].text}`}>
                      {STATUS_META[selected.status].label}
                    </span>
                  </div>
                  <div className="font-semibold text-sm text-gray-900">{selected.jobNumber} · {selected.shiftType} shift</div>
                  <div className="text-xs text-gray-500 mt-0.5">{selected.siteAddress} · {fmt(selected.assessmentDate)}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => generateSRAPDF(selected)}
                    className="btn-secondary text-xs flex items-center gap-1">
                    <Download className="w-3.5 h-3.5" />PDF
                  </button>
                  <button onClick={() => setSelected(null)}><X className="w-4 h-4 text-gray-400" /></button>
                </div>
              </div>

              {/* Detail sections */}
              <div className="p-4 space-y-4 text-sm">

                {/* Weather */}
                <div>
                  <div className="text-xs font-bold text-[#1F3864] uppercase tracking-wide mb-2">Environmental</div>
                  <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 text-sm flex items-center gap-3 flex-wrap">
                    <span>{WEATHER_META[selected.weather].icon} {WEATHER_META[selected.weather].label}</span>
                    <span className="flex items-center gap-1"><Thermometer className="w-3.5 h-3.5" />{selected.temperature}°C</span>
                    <span className="flex items-center gap-1"><Wind className="w-3.5 h-3.5" />{selected.windSpeed}</span>
                    <span>{selected.lighting}</span>
                  </div>
                  {WEATHER_META[selected.weather].alert && (
                    <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800">
                      {WEATHER_META[selected.weather].alert}
                    </div>
                  )}
                  {selected.siteConditions && (
                    <div className="mt-2 text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{selected.siteConditions}</div>
                  )}
                </div>

                {/* Hazards */}
                {selected.hazards.filter(h => h.present).length > 0 && (
                  <div>
                    <div className="text-xs font-bold text-[#1F3864] uppercase tracking-wide mb-2">Hazards identified</div>
                    <div className="space-y-1.5">
                      {selected.hazards.filter(h => h.present).map(h => (
                        <div key={h.id} className="border border-amber-200 bg-amber-50/50 rounded-lg px-3 py-2">
                          <div className="font-medium text-sm text-gray-800">{h.label}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{h.category} · {h.control || 'No control recorded'}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Controls confirmed */}
                <div>
                  <div className="text-xs font-bold text-[#1F3864] uppercase tracking-wide mb-2">Controls confirmed</div>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: 'SWMS active',       ok: selected.swmsActive,           detail: selected.swmsRef },
                      { label: 'Traffic control',    ok: selected.trafficControlActive, detail: selected.tmrActive },
                      { label: 'Permits held',       ok: selected.permitsHeld,          detail: selected.permitsRef },
                      { label: 'Exclusion zones',    ok: selected.exclusionZonesSet,    detail: '' },
                      { label: 'White cards',        ok: selected.whiteCardsConfirmed,  detail: '' },
                      { label: 'Inductions',         ok: selected.inductionsConfirmed,  detail: '' },
                    ].map(({ label, ok, detail }) => (
                      <div key={label} className={`rounded-lg px-2.5 py-1.5 text-xs border ${ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
                        {ok ? <CheckCircle className="w-3 h-3 inline mr-1 text-green-600" /> : <X className="w-3 h-3 inline mr-1 text-gray-400" />}
                        {label}{detail ? ` — ${detail}` : ''}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Plant + Personnel */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs font-bold text-[#1F3864] uppercase tracking-wide mb-2">Plant on site</div>
                    {selected.plantOnSite.length > 0
                      ? <ul className="space-y-1">{selected.plantOnSite.map(p => <li key={p} className="text-xs text-gray-700 flex items-center gap-1"><Truck className="w-3 h-3 text-gray-400" />{p}</li>)}</ul>
                      : <p className="text-xs text-gray-400">None recorded</p>}
                  </div>
                  <div>
                    <div className="text-xs font-bold text-[#1F3864] uppercase tracking-wide mb-2">Personnel</div>
                    <p className="text-xs text-gray-700"><Users className="w-3 h-3 inline mr-1 text-gray-400" />{selected.workerCount} workers</p>
                    <p className="text-xs text-gray-700 mt-1">Supervisor: {selected.supervisorName}</p>
                    <p className="text-xs text-gray-700 mt-1">FA officer: {selected.firstAidOfficerName}</p>
                    <p className="text-xs text-gray-700 mt-1">Muster: {selected.musterPoint}</p>
                  </div>
                </div>

                {/* Notes */}
                {selected.additionalNotes && (
                  <div>
                    <div className="text-xs font-bold text-[#1F3864] uppercase tracking-wide mb-2">Additional notes</div>
                    <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{selected.additionalNotes}</p>
                  </div>
                )}

                {/* Audit trail */}
                <div className="bg-gray-50 border border-gray-100 rounded-lg p-3 text-xs space-y-1 text-gray-600">
                  <div>Prepared: {fmtT(selected.preparedAt)} · {selected.preparedByName}</div>
                  {selected.reviewedAt && <div>Reviewed: {fmtT(selected.reviewedAt)} · {selected.reviewedByName}</div>}
                  {selected.closedAt && <div>Closed: {fmtT(selected.closedAt)}</div>}
                </div>
              </div>

              {/* Actions */}
              {selected.status === 'OPEN' && canReview && (
                <div className="border-t px-4 py-3 bg-amber-50 flex gap-2 items-center">
                  <span className="flex-1 text-xs text-amber-800">WHSO review required before next shift.</span>
                  <button onClick={() => handleReview(selected.id)} className="btn-primary text-xs flex items-center gap-1">
                    <CheckCircle className="w-3.5 h-3.5" />Mark reviewed
                  </button>
                </div>
              )}
              {selected.status === 'REVIEWED' && canReview && (
                <div className="border-t px-4 py-3 bg-blue-50 flex gap-2 items-center">
                  <span className="flex-1 text-xs text-blue-800">Reviewed. Close when shift is complete.</span>
                  <button onClick={() => handleClose(selected.id)} className="btn-secondary text-xs">Close SRA</button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
